import React from "react";

import {
  RESULT_MODE_CONFIG,
  getResultModeConfigForVariant,
} from "../../utils/results/resultModeConfig";
import {
  getResultModeIcon,
  getResultModeLabel,
} from "../../utils/results/resultModePresentation";
import { buildResultPath } from "../../utils/results/resultNavigation";
import {
  RECORDING_QUALITY_OVERALL_STATES,
  buildRecordingQualityCheck,
  getRecordingQualitySummaryCopy,
  summarizeRecordingQualityChecks,
} from "../../utils/results/recordingQuality";

function isResultFeatureVisible(resultFeatures, featureKey) {
  if (!resultFeatures || typeof resultFeatures !== "object") {
    return true;
  }

  return resultFeatures?.[featureKey] === true;
}

function QualityMessage({ children }) {
  if (!children) return null;

  return (
    <span className="w-full text-xs leading-tight text-greyscale-3">
      {children}
    </span>
  );
}

function InlineRepeatMessage({ title, body }) {
  if (!title || !body) return null;

  return (
    <div className="flex w-full min-w-0 flex-wrap items-center gap-1 text-sm leading-relaxed whitespace-normal">
      <span className="font-semibold text-amber-800">{title}</span>
      <span className="font-normal text-typography" aria-hidden="true">
        ·
      </span>
      <span className="font-normal text-typography">{body}</span>
    </div>
  );
}

function RecordingQualityCardSummary({ summary, t }) {
  const summaryCopy = getRecordingQualitySummaryCopy(t, summary, {
    surface: "history",
  });

  if (!summaryCopy) {
    return null;
  }

  const isRepeat =
    summary.overallState === RECORDING_QUALITY_OVERALL_STATES.REPEAT;
  const hasInlineRepeatBody = isRepeat && summaryCopy.title && summaryCopy.body;

  return (
    <div
      className="flex w-full min-w-0 flex-col"
      aria-label={summaryCopy.accessibleLabel}
    >
      {hasInlineRepeatBody ? (
        <InlineRepeatMessage
          title={summaryCopy.title}
          body={summaryCopy.body}
        />
      ) : summaryCopy.title ? (
        <span className="min-w-0 text-sm font-medium leading-relaxed whitespace-normal text-typography">
          {summaryCopy.title}
        </span>
      ) : null}
      {summaryCopy.body && !hasInlineRepeatBody ? (
        <span className="w-full text-sm leading-relaxed whitespace-normal text-typography">
          {summaryCopy.body}
        </span>
      ) : null}
    </div>
  );
}

function buildResultCardQualitySummary(row, t, resultFeatures = null) {
  const checks = [];
  const recordingTimestamp = row?.timestamp || null;
  const respiratoryQuality = row.rrQuality || row.signalQuality;
  const showSignalQuality = isResultFeatureVisible(
    resultFeatures,
    "signalQuality",
  );
  const showSpirometryQuality = isResultFeatureVisible(
    resultFeatures,
    "spirometryQuality",
  );
  const showCardiacQuality = isResultFeatureVisible(
    resultFeatures,
    "cardiacQuality",
  );

  if (row.variant === RESULT_MODE_CONFIG.spirometry.variant) {
    if (showSignalQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "signal",
          label: t("result.spiro.signalQuality", "Signal Quality"),
          rawStatus: row.signalQuality,
          recordingTimestamp,
        }),
      );
    }

    if (showSpirometryQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "spirometry",
          label: t("result.spiro.spirometryQuality", "Spirometry Quality"),
          rawStatus: row.spirometryQuality,
          recordingTimestamp,
        }),
      );
    }
  } else if (row.variant === RESULT_MODE_CONFIG.postExertion.variant) {
    if (showSignalQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "respiratory",
          label: t(
            "result.postExertion.respiratorySignalQuality",
            "Respiratory Signal Quality",
          ),
          rawStatus: respiratoryQuality,
          recordingTimestamp,
        }),
      );
    }
  } else if (row.variant === RESULT_MODE_CONFIG.pulse.variant) {
    if (showCardiacQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "cardiac",
          label: t("result.cardiopulmonary.cardiacQuality", "Cardiac Quality"),
          rawStatus: row.hrQuality,
          recordingTimestamp,
        }),
      );
    }
  } else if (showCardiacQuality || showSignalQuality) {
    if (showCardiacQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "cardiac",
          label: t("result.cardiopulmonary.cardiacQuality", "Cardiac Quality"),
          rawStatus: row.hrQuality,
          recordingTimestamp,
        }),
      );
    }

    if (showSignalQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "respiratory",
          label: t(
            "result.cardiopulmonary.respiratoryQuality",
            "Respiratory Quality",
          ),
          rawStatus: respiratoryQuality,
          recordingTimestamp,
        }),
      );
    }
  }

  return summarizeRecordingQualityChecks(checks);
}

/**
 * Builds the per-mode quality chips + guidance messages shown in the body of a
 * result list card. Returns `{ chips, messages }` (each an array of JSX nodes)
 * so the caller can render the chips as a wrap row with any guidance messages
 * stacked underneath — the messages are always last, never interleaved.
 */
function buildResultQualityDetails(
  row,
  t,
  resultFeatures = null,
  _qualityStatusPillsEnabled = false,
) {
  if (!row) return { chips: [], messages: [] };

  // Collect chips and messages separately so the caller can render the pass/fail
  // chips as a wrap row with any guidance messages stacked underneath — rather
  // than interleaved per quality metric (chip, message, chip, …).
  const chips = [];
  const messages = [];
  const addMessage = (key, message) => {
    if (!message) return;

    messages.push(<QualityMessage key={key}>{message}</QualityMessage>);
  };

  if (row.uploadFailed) {
    messages.push(
      <InlineRepeatMessage
        key="upload-failed"
        title={t(
          "result.recordingQuality.summary.repeatCardTitle",
          "Please repeat",
        )}
        body={t("result.uploadFailedTitle", "Upload failed")}
      />,
    );
    return { chips, messages };
  }

  if (row.variant === RESULT_MODE_CONFIG.manualAudio.variant) {
    if (row.manualAudioComplete) {
      addMessage(
        "manual-audio-complete",
        t(
          "result.manualAudio.completeTitle",
          "Manual audio recording complete",
        ),
      );
    }

    return { chips, messages };
  }

  const summary = buildResultCardQualitySummary(row, t, resultFeatures);

  if (summary.overallState !== RECORDING_QUALITY_OVERALL_STATES.UNAVAILABLE) {
    messages.push(
      <RecordingQualityCardSummary
        key="recording-quality"
        summary={summary}
        t={t}
      />,
    );
  }

  return { chips, messages };
}

function buildResultCardMeta(row) {
  const time = String(row?.time || "").trim();
  const date = String(row?.date || "").trim();

  return {
    meta: [time, date].filter(Boolean).join(" "),
    secondaryMeta: "",
  };
}

/**
 * Maps a measurement row into the view model consumed by the results list
 * (label, date/time meta, mode icon, quality chips, and the detail-page link).
 * Returns null for rows without an id or a recognised recording mode.
 */
export function buildResultListItem(
  row,
  t,
  resultFeatures = null,
  qualityStatusPillsEnabled = false,
) {
  const config = getResultModeConfigForVariant(row?.variant);
  const workflowStage =
    typeof row?.rawMeasurement?.workflow?.stage === "string"
      ? row.rawMeasurement.workflow.stage.trim().toLowerCase()
      : null;

  if (!row?.id || !config) {
    return null;
  }

  if (
    row.variant === RESULT_MODE_CONFIG.sixMinuteWalk.variant &&
    workflowStage === "pre"
  ) {
    return null;
  }

  const { meta, secondaryMeta } = buildResultCardMeta(row);

  return {
    id: `${row.variant}:${row.id}`,
    label: getResultModeLabel(row.variant, t),
    meta,
    secondaryMeta,
    details: buildResultQualityDetails(
      row,
      t,
      resultFeatures,
      qualityStatusPillsEnabled,
    ),
    icon: getResultModeIcon(row.variant),
    to: buildResultPath({
      variant: row.variant,
      docid: row.id,
      context: config.context,
      tab: config.tab,
    }),
  };
}
